import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { clearOrgCache, getCurrentOrganization } from "../lib/organizationContext";
import { queryKeys } from "../lib/queryClient";
import type { User as SupabaseUser } from "@supabase/supabase-js";

const USER_STORAGE_KEY = "ayana_affiliate_user";

function loadStoredUser<T>(): T | null {
  const saved = localStorage.getItem(USER_STORAGE_KEY);
  if (saved == null) return null;
  try {
    return JSON.parse(saved) as T;
  } catch (error) {
    console.error("Error parsing saved user from localStorage:", error);
    localStorage.removeItem(USER_STORAGE_KEY);
    return null;
  }
}

interface User {
  id: string;
  email: string;
  name: string;
  affiliateCode?: string;
  affiliateId?: string;
  affiliateProgramId?: string; // Added: the program ID for querying
  organizationId?: string;
  customReferralUrl?: string;
  commissionRate?: number;
  role: "affiliate" | "admin";
  status: "active" | "suspended" | "pending" | "rejected" | "unauthorized";
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

interface SignupData {
  email: string;
  password: string;
  name: string;
  organizationId?: string;
  referredBy?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => loadStoredUser<User>());
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // Persist user to localStorage whenever it changes (mirrors the old
  // Recoil localStorage effect).
  useEffect(() => {
    if (user === null) {
      localStorage.removeItem(USER_STORAGE_KEY);
    } else {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    }
  }, [user]);

  // Helper function to fetch affiliate data for authenticated user.
  //
  // getSession() (in initializeAuth below) and onAuthStateChange's own
  // initial firing both resolve on mount and each call this -- a Supabase
  // gotcha, not a bug here -- so without dedup this fires the same
  // `affiliates` query twice (sometimes three times counting React
  // StrictMode's double-invoke) concurrently on every page load.
  // queryClient.fetchQuery shares one in-flight request/result across
  // those concurrent callers for the same user id. staleTime is short
  // (10s, just long enough to cover that startup race) rather than the
  // longer caching this app uses elsewhere: this affiliate PROFILE data
  // (name, program id, status) rarely changes, but a previous version of
  // this function cached it indefinitely and that was the direct cause of
  // a real incident (dashboard showing 0 clicks after real conversions
  // came in) -- that specific data (click/conversion counts) now lives in
  // its own properly-invalidated query (dashboardStats), not here, but the
  // short staleTime keeps this fetch itself close to the old
  // always-fresh behavior as a safety margin.
  const fetchAffiliateData = async (supabaseUser: SupabaseUser) => {
    try {
      const affiliate = await queryClient.fetchQuery({
        queryKey: queryKeys.affiliateProfile(supabaseUser.id),
        staleTime: 10_000,
        queryFn: async () => {
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("Fetch timeout")), 5000)
          );

          const fetchPromise = supabase
            .from("affiliates")
            .select(
              `
              *,
              affiliate_programs (
                id,
                affiliate_code,
                status,
                organization_id,
                commission_rate,
                custom_settings
              )
            `
            )
            .eq("user_id", supabaseUser.id)
            .single();

          const { data, error } = (await Promise.race([
            fetchPromise,
            timeoutPromise,
          ])) as any;

          if (error && error.code !== "PGRST116") {
            console.error("Error fetching affiliate data:", error);
            // Still return basic user data even if there's an error
          }

          return data ?? null;
        },
      });

      if (affiliate) {
        // Get the current organization ID to select the correct program.
        // Routed through the same query cache useOrganization() uses
        // (queryClient.fetchQuery, not a direct getCurrentOrganization()
        // call) so this shares the one in-flight request/result with
        // AffiliateHeader and whichever page is mounting alongside it,
        // instead of racing them as a third independent caller.
        let currentOrgId: string | null = null;
        try {
          const org = await queryClient.fetchQuery({
            queryKey: queryKeys.organization,
            queryFn: getCurrentOrganization,
            staleTime: 5 * 60_000,
          });
          currentOrgId = org?.id ?? null;
        } catch (error) {
          console.error("Error getting current organization ID:", error);
        }

        // Resolve the program and status strictly against currentOrgId --
        // a program the affiliate has with a DIFFERENT organization must
        // never be used here. This used to fall back to the affiliate's
        // first approved program from ANY organization whenever none
        // matched the current one, which let e.g. a PatentTrail affiliate
        // log in and see real PatentTrail data (code, commission rate,
        // referral stats) while browsing on affiliate.youversity.io --
        // wrong-domain logins were silently accepted instead of rejected.
        let activeProgram: any;
        let userStatus: "active" | "suspended" | "pending" | "rejected" | "unauthorized";

        if (currentOrgId) {
          const programForCurrentOrg = affiliate.affiliate_programs?.find(
            (p: any) => p.organization_id === currentOrgId
          );

          if (programForCurrentOrg) {
            activeProgram = programForCurrentOrg.status === "approved" ? programForCurrentOrg : undefined;
            if (programForCurrentOrg.status === "approved") userStatus = "active";
            else if (programForCurrentOrg.status === "suspended") userStatus = "suspended";
            else if (programForCurrentOrg.status === "rejected") userStatus = "rejected";
            else userStatus = "pending";
          } else {
            // Not a member of this organization at all, regardless of
            // whether they're an approved affiliate elsewhere.
            userStatus = "unauthorized";
          }
        } else {
          // Could not determine which organization this domain belongs to
          // (a getCurrentOrganization() failure, not the user's fault) --
          // fall back to any approved program so a real affiliate isn't
          // locked out by an infra hiccup, rather than enforcing an org
          // check we're not actually able to make right now.
          activeProgram = affiliate.affiliate_programs?.find((p: any) => p.status === "approved");
          userStatus = activeProgram ? "active" : "pending";
        }

        const userData = {
          id: supabaseUser.id,
          email: supabaseUser.email!,
          name: affiliate.full_name,
          affiliateCode: activeProgram?.affiliate_code,
          affiliateId: affiliate.id,
          affiliateProgramId: activeProgram?.id, // Store the program ID
          organizationId: activeProgram?.organization_id,
          customReferralUrl:
            activeProgram?.custom_settings?.custom_referral_url,
          commissionRate: activeProgram?.commission_rate,
          role: "affiliate" as const,
          status: userStatus,
          createdAt: affiliate.created_at,
        };

        return userData;
      }

      // app_metadata only -- it's server-controlled, unlike user_metadata,
      // which the signed-in user can set on themselves.
      const isAdmin = supabaseUser.app_metadata?.role === "admin";

      // Always return a user object, never null
      const userData = {
        id: supabaseUser.id,
        email: supabaseUser.email!,
        name:
          supabaseUser.user_metadata?.full_name ||
          supabaseUser.user_metadata?.name ||
          supabaseUser.email!.split("@")[0],
        role: isAdmin ? ("admin" as const) : ("affiliate" as const),
        status: "active" as const,
        createdAt: supabaseUser.created_at,
      };

      return userData;
    } catch (error) {
      console.error("Error in fetchAffiliateData:", error);
      // Always return a basic user object, never null
      return {
        id: supabaseUser.id,
        email: supabaseUser.email!,
        name:
          supabaseUser.user_metadata?.full_name ||
          supabaseUser.user_metadata?.name ||
          supabaseUser.email!.split("@")[0],
        role: "affiliate" as const,
        status: "active" as const,
        createdAt: supabaseUser.created_at,
      };
    }
  };

  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      console.warn("Auth initialization timeout - forcing loading to false");
      setLoading(false);
    }, 10000); // 10 seconds max

    // Check active session on mount
    const initializeAuth = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          const userData = await fetchAffiliateData(session.user);
          setUser(userData);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        clearTimeout(loadingTimeout);
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const userData = await fetchAffiliateData(session.user);
        setUser(userData);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Supabase's own wording ("Invalid login credentials") is fine,
        // but keep it consistent with the admin app's login, which uses
        // this same phrasing.
        if (error.status === 400) {
          throw new Error("Invalid credentials. Please confirm your details.");
        }
        throw error;
      }

      // Don't manually set user here - let onAuthStateChange handle it
      // This prevents double fetching and infinite loops
    } catch (error: any) {
      console.error("Login error:", error);
      throw new Error(error.message || "Failed to login");
    }
  };

  const signup = async (signupData: SignupData) => {
    try {
      // Determine the correct redirect URL
      const redirectUrl = window.location.origin + "/auth";

      const { data, error } = await supabase.auth.signUp({
        email: signupData.email,
        password: signupData.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            name: signupData.name,
            referred_by: signupData.referredBy,
          },
        },
      });

      if (error) throw error;

      // Note: Supabase may require email confirmation before user can login
      // The affiliate record will be created separately through the application process

      // Don't manually set user here - let onAuthStateChange handle it
      // This prevents double fetching and infinite loops
    } catch (error: any) {
      console.error("Signup error:", error);
      throw new Error(error.message || "Failed to sign up");
    }
  };

  const logout = async () => {
    try {
      // Clear organization cache to prevent stale data
      clearOrgCache();

      // Sign out from Supabase
      await supabase.auth.signOut();

      // Clear user state
      setUser(null);

      // Drop every cached query (dashboard stats, earnings, affiliate
      // profile) so the next affiliate to sign in on this browser never
      // sees the previous one's data, even briefly.
      queryClient.clear();
    } catch (error) {
      console.error("Logout error:", error);
      // Still clear local state even if signOut fails
      setUser(null);
      clearOrgCache();
      queryClient.clear();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        signup,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
